import React, { SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PortalLoyaltyProgramWelcome from "./PortalLoyaltyProgramWelcome";
import "./portalLoyaltyProgram.scss";
import { ButtonNew as Button } from "components/buttonNew";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faStar } from "@fortawesome/pro-light-svg-icons";
import { ILoyaltyCustomer, ILoyaltyProgram, IReward, ITier } from "redux/reducers/models/loyalty";
import axios, { CancelToken } from "axios";
import { GetLoyaltyProgram, GetLoyaltyProgramCustomer, GetLoyaltyProgramCustomerExpiringPoints, GetLoyaltyProgramRewards, GetLoyaltyProgramTiers, PostLoyaltyProgramCustomer } from "api/rpc/2024-04/customer/loyaltyProgram/loyaltyProgram";
import { StatusCode } from "api/protocols";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import ReactDOM from "react-dom";
import Spin from "components/spin/spin";
import PortalLoyaltyProgramView from "./PortalLoyaltyProgramView";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import useModal from "hooks/modals/useModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faGift } from "@fortawesome/pro-regular-svg-icons";
import Checkbox from "components/form/checkbox/Checkbox";
import Input from "components/form/input";
import classNames from "classnames";
import Popup from "components/popup/Popup";
import { GetClient, IClient } from "api/rpc/2024-04/customer/client";
import { GetFacility } from "api/rpc/2024-04/customer/facility";
import { IFacility } from "redux/reducers/models/facility";

export default function PortalLoyaltyProgram() {
    const {t} = useTranslation();
    const dispatch = useAppDispatch(); 
    const [loyaltyPrograms, setLoyaltyPrograms] = useState<Array<ILoyaltyProgram>>(undefined);
    const [selectedProgram, setSelectedProgram] = useState<ILoyaltyProgram>(undefined); 
    const [loyaltyCustomer, setLoyaltyCustomer] = useState<ILoyaltyCustomer>(undefined);
    const [expiringPoints, setExpiringPoints] = useState<number>(0);
    const [rewards, setRewards] = useState<Array<IReward>>([]);
    const [tiers, setTiers] = useState<Array<ITier>>([]);
    const [loyaltyProgramQuery, setLoyaltyProgramQuery] = useState<string>("");
    const [clients, setClients] = useState<Array<IClient & {facilities: Array<IFacility>; loyaltyProgram: ILoyaltyProgram; open: boolean}>>([]);
    const { state: programModal, updateModal: updateProgramModal, closeModal: closeProgramModal } = useModal<{selectedProgram: ILoyaltyProgram}>({selectedProgram: null});
    const { state: confirmModal, updateModal: updateConfirmModal, closeModal: closeConfirmModal } = useModal<{selectedProgram: ILoyaltyProgram}>({selectedProgram: null});

    useEffect(() => {
        const source = axios.CancelToken.source();
        void getLoyaltyPrograms(source.token);
        return () => source.cancel();
    }, []);

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (selectedProgram) {
            void getLoyaltyProgramCustomer(source.token);
        }
        return () => source.cancel();
    }, [selectedProgram]);

    async function getLoyaltyPrograms(cancelToken?: CancelToken, loyaltyProgramId?: number) {
        const res = await GetLoyaltyProgram({include_enrollment: true}, false, cancelToken);
        if (res?.status !== StatusCode.OK){
            setLoyaltyPrograms([]);
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting loyalty programs")));
            return;
        }
        const loyaltyPrograms: Array<ILoyaltyProgram> = res?.data;
        
        if (loyaltyPrograms && loyaltyPrograms?.length > 0) {
            const enrolledLoyaltyProgram = loyaltyProgramId ? loyaltyPrograms?.find(program => program?.id === loyaltyProgramId) : loyaltyPrograms?.find(program => program?.is_enrolled);
            //Find the first loyalty program that the user is enrolled in and load it
            if (enrolledLoyaltyProgram) {
                setSelectedProgram(enrolledLoyaltyProgram);
            } else {
                setLoyaltyCustomer(null);
            }
        }

        void getClients(cancelToken, loyaltyPrograms);
        
        setLoyaltyPrograms(loyaltyPrograms);
    }
    
    async function getLoyaltyProgramCustomer(cancelToken?: CancelToken) {
        //loader
        if (loyaltyCustomer !== undefined) {
            setLoyaltyCustomer(undefined);
        }
        const res = await GetLoyaltyProgramCustomer({loyalty_program_id: selectedProgram?.id}, false, cancelToken);
        if (res?.status !== StatusCode.OK){
            setLoyaltyCustomer(undefined);
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            //Customer not found
            if (res?.status === StatusCode.NOT_FOUND) {
                setLoyaltyCustomer(null);
                return;
            }
            dispatch(showError(t("Error getting loyalty program customer")));
            return;
        }
        let rewards: Array<IReward> = [];
        let tiers: Array<ITier> = [];
        let expiringPoints: number = 0;
        if (res?.data) {
            const expiringRes = await GetLoyaltyProgramCustomerExpiringPoints({loyalty_program_id: selectedProgram?.id}, false, cancelToken);
            if (expiringRes?.status !== StatusCode.OK) {
                setExpiringPoints(0);
                if (cancelToken && cancelToken?.reason) {
                    return;
                }
                dispatch(showError(t("Error getting expiring points")));
                return;
            }
            expiringPoints = expiringRes?.data;
            
            const rewardsRes = await GetLoyaltyProgramRewards({client_id: selectedProgram?.client_id}, false, cancelToken);
            if (rewardsRes?.status !== StatusCode.OK) {
                setRewards([]);
                if (cancelToken && cancelToken?.reason) {
                    return;
                }
                dispatch(showError(t("Error getting rewards")));
                return;
            }
            rewards = rewardsRes?.data;
            if (selectedProgram?.type === "tier_based") {
                const tierRes = await GetLoyaltyProgramTiers({client_id: selectedProgram?.client_id}, false);
                if (tierRes?.status !== StatusCode.OK) {
                    setTiers([]);
                    if (cancelToken && cancelToken?.reason) {
                        return;
                    }
                    dispatch(showError(t("Error getting tiers")));
                    return;
                }
                tiers = tierRes?.data;
            }
        }
        ReactDOM.unstable_batchedUpdates(() => {
            setExpiringPoints(expiringPoints);
            setRewards(rewards);
            setTiers(tiers);
            setLoyaltyCustomer(res?.data as SetStateAction<ILoyaltyCustomer>);
        });
    }

    async function getClients(cancelToken: CancelToken, loyaltyPrograms: Array<ILoyaltyProgram>) {
        const [clientRes, facilityRes] = await Promise.all([GetClient(null ,false, cancelToken), GetFacility({order_by: "long_name"}, false, cancelToken)]);
        if (clientRes?.status !== StatusCode.OK) {
            dispatch(showError(t("Error getting clients")));
            return;
        }

        if (facilityRes?.status !== StatusCode.OK) {
            dispatch(showError(t("Error getting facilities")));
            return;
        }
        const clients = clientRes?.data;
        const facilities = facilityRes?.data;

        const updatedClients = clients?.reduce((clients, client) => {
            const clientFacilities = facilities?.filter(facility => facility?.client_id === client?.id);
            if (clientFacilities?.length > 0) {
                const clientLoyaltyProgram = loyaltyPrograms?.find(program => program?.client_id === client?.id);
                if (!clientLoyaltyProgram) {
                    //if loyalty program doesn't exist, don't display client
                    return clients;
                }
                const updatedClient = {...client, open: false, facilities: clientFacilities, loyaltyProgram: clientLoyaltyProgram};
                return [...clients, updatedClient];
            } else {
                return clients;
            }
        }, [] as Array<IClient & {facilities: Array<IFacility>; loyaltyProgram: ILoyaltyProgram; open: boolean}>);
        setClients(updatedClients);
    }

    async function joinProgram(clientId: number, programId: number) {
        const res = await PostLoyaltyProgramCustomer({client_id: clientId}, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(t("Error joining loyalty program")));
            return;
        }
        void closeConfirmModal();
        void closeProgramModal();
        await getLoyaltyPrograms(undefined, programId);
        dispatch(showSuccess(t("Successfully joined loyalty program")));
    }

    function showFacilities(e: React.MouseEvent, client: IClient & {facilities: Array<IFacility>; loyaltyProgram: ILoyaltyProgram; open: boolean}) {
        e.stopPropagation();
        const updatedClients = [...clients]?.map(currClient => currClient?.id === client?.id ? {...client, open: !currClient?.open} : currClient);
        setClients(updatedClients);
    }

    //Determine which app store to navigate to based on the user's device
    function navigateToAppStore() {
        //Apple device -> open app store
        if (/iphone/i.test(navigator.userAgent) || /ipad/i.test(navigator.userAgent) || /macintosh/i.test(navigator.userAgent)) {
            window.open("https://apps.apple.com/kg/app/tee-on/id6476159748");
        } else {
            //Android or windows -> open play store
            window.open("https://play.google.com/store/apps/details?id=com.teeon");
        }
    }

    return (
        <div>
            <div className="portal-loyalty-program-header">
                <div>
                    <p className="portal-loyalty-program-header-primary">{selectedProgram?.name ?? t("Loyalty Program")}</p>
                    <p className="portal-loyalty-program-header-secondary">{t("View and manage your point balance, tier progress and recent activity")}</p>
                </div>

                <div style={{display: "flex", gap: "1rem"}}>
                <Button type="secondary" size="medium" disabled={!loyaltyPrograms || loyaltyPrograms?.length === 0 || !loyaltyCustomer} onClick={() => updateProgramModal({isOpen: true, selectedProgram: null})}>{t("Select Loyalty Program")} <FontAwesomeIcon icon={faChevronDown} size="1x" /></Button>
                    <Button type="primary" size="medium" onClick={navigateToAppStore}>{t("Download App")}</Button>
                </div>
                
            </div>
            {!loyaltyPrograms || loyaltyCustomer === undefined ? (
                <div style={{width: "50px", display: "flex", margin: "auto"}}>
                    <Spin/>
                </div>
            ) : loyaltyCustomer === null ? (
                <PortalLoyaltyProgramWelcome refreshPrograms={getLoyaltyPrograms} loyaltyPrograms={loyaltyPrograms}/>
            ) : <PortalLoyaltyProgramView loyaltyCustomer={loyaltyCustomer} expiringPoints={expiringPoints} tiers={tiers} rewards={rewards} loyaltyProgram={selectedProgram} />}
            
            <Portal isMounted={programModal?.isOpen}>
                <Sheet
                    open={programModal?.isOpen}
                    title={t("Select a Loyalty Program")}
                    supportText={t("Choose between different loyalty programs for eligible courses")}
                    onCancel={() => {
                        void closeProgramModal(); 
                        setLoyaltyProgramQuery("");
                    }}
                    onOk={() => {
                        setSelectedProgram(programModal?.selectedProgram); 
                        void closeProgramModal();
                    }}
                    size="small"
                    okText={t("Confirm")}
                    okDisabled={!programModal?.selectedProgram}
                    >
                        <Input labelHidden placeholder={t("Search loyalty programs...")} value={loyaltyProgramQuery} onChange={e => setLoyaltyProgramQuery(e.target.value)} onSearchClear={() => setLoyaltyProgramQuery("")}/>
                        <br />
                        <div className="program-facility-container">
                        {clients?.filter(client => client?.loyaltyProgram?.name?.toLowerCase()?.includes(loyaltyProgramQuery?.toLowerCase()))?.map((client, index) => {
                            return (
                                <div key={index} className={classNames("program-facility-card", {"program-facility-card-selected": programModal?.selectedProgram?.id === client?.loyaltyProgram?.id})} 
                                onClick={() => client?.loyaltyProgram?.is_enrolled ? updateProgramModal({selectedProgram: client?.loyaltyProgram}) : updateConfirmModal({isOpen: true, selectedProgram: client?.loyaltyProgram})}>
                                    <div className="program-facility-card-group">
                                    <div className="flex items-center gap-4">
                                        {client?.logo_source_url ? (
                                            <img alt={t("Program Logo")} src={client?.logo_source_url} className="program-facility-logo" />
                                        ) : (
                                            <div><FontAwesomeIcon icon={faGift} /></div>
                                        )}
                                        
                                        <div className="flex-col">
                                            <p className="program-facility-primary">{client?.loyaltyProgram?.name}</p>
                                            {client?.facilities?.length > 1 ? (
                                                <p className="program-facility-secondary">{client?.facilities?.length} {t("participating golf courses")}</p>
                                            ) : null}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-4 ">
                                        {client?.facilities?.length > 1 ? (
                                             <FontAwesomeIcon fixedWidth icon={client?.open ? faChevronUp : faChevronDown} onClick={e => showFacilities(e, client)} />
                                        ) : null}
                                        {client?.loyaltyProgram?.is_enrolled ? (
                                             <Checkbox 
                                             circle 
                                             size="small" 
                                             labelHidden 
                                             onChange={() => client?.loyaltyProgram?.is_enrolled ? updateProgramModal({selectedProgram: client?.loyaltyProgram}) : {}}
                                             checked={client?.loyaltyProgram?.id === programModal?.selectedProgram?.id}
                                         />
                                        ) : <Button type="text" size="small" onClick={() => updateConfirmModal({isOpen: true, selectedProgram: client?.loyaltyProgram})}>{t("Enroll")}</Button>}
                                    </div>
                                    </div>
                                    {client?.open ? (
                                        <div className="program-facilities-container">
                                            {client?.facilities?.map((facility, index) => {
                                                return (
                                                    <div key={index} className="loyalty-tag">
                                                        <p className="loyalty-tag-text">{facility?.long_name}</p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                </Sheet>
            </Portal>

            <Popup 
                stacked
                onCancel={closeConfirmModal} 
                onOk={() => joinProgram(confirmModal?.selectedProgram?.client_id, confirmModal?.selectedProgram?.id)} 
                open={confirmModal?.isOpen} title={t("Join Loyalty Program")} 
                description={`Are you sure you want to join ${confirmModal?.selectedProgram?.name}?`} 
                type="info"/>
        </div>
    );
}